import { defineStore, type _GettersTree } from 'pinia';
import type { UnwrapRef } from 'vue';
import user from 'service/user';
import usePwaClient from 'core/hooks/usePwaClient';
import {
    AppConfigType,
    IdentificationListType,
    LoginConfigType,
    rouletteDataType,
    SystemDataType,
    UserInfoType,
    CarouselListType,
    RouletteFindTimeListType,
    LeaderboardListType,
    DailyTasksListType,
    SumPreferentialMoneyType,
    SliderActiveType,
    getFastListType,
    UserImgListType,
    VipListModelData,
    HomeActivityType,
    HomeNoticeListType,
    OpenHomeActivityIdType,
    MessageDialogType,
} from 'types/appConfig';
import HiCache from 'utils/cache';
import HiStance from 'utils/HiStance';
import { useRedAppStore as useAppStore } from 'store/modules/redAppStore';
import sysConfig from 'service/sysConfig';

export interface UserState {
    userInfo: UserInfoType | null;
    identificationTypeList: IdentificationListType[];
    token: string | undefined;
    salt: string;
    refreshToken: string | undefined;
    loginConfig: LoginConfigType | {};
    rouletteList: rouletteDataType | {};
    homeCarouselList: CarouselListType[];
    sliderActive: SliderActiveType[];
    openLoginDialogType: boolean;
    openSlider: boolean;
    openMessage: boolean;
    openLanguageType: boolean;
    openForgotDialogType: boolean;
    openTurnTableDialogType: boolean;
    rouletteFindTimeList: RouletteFindTimeListType[];
    leaderboardList: LeaderboardListType[];
    dailyTasksList: DailyTasksListType[];
    sumPreferentialMoney: SumPreferentialMoneyType | {};
    activityDetail: SliderActiveType | {};
    userBalance: string;
    agentMoney: string;
    getFastList: getFastListType[];
    userImgList: UserImgListType[];
    readNum: number;
    userMainBanlance: any;
    turnTableId: number | string;
    loginDialogType: number;
    vipList: VipListModelData[];
    vipListInfo: string;
    homeActivity: HomeActivityType[];
    homeNoticeList: HomeNoticeListType[];
    openNoticeDialog: boolean;
    openActivityNotice: OpenHomeActivityIdType;
    unreadCount: number;
    messageDialog: MessageDialogType[];
    isLoginSignIn: boolean; // 是否显示登录宝箱
    signInPreferentialActivityId: number | null; // 签到活动 ID
    signInLevel: number | null; // 宝箱等级 (1-6)
    signInIcon: string | null; // 宝箱图片
    signInDynamicIcon: string | null; // 宝箱动效图片
    isOpenHiddenTreasureChest: boolean; // 是否显示隐藏宝箱
    hiddenTreasureChestDynamicIcon: string | null; // 隐藏宝箱动效图片
    hiddenTreasureChestIcon: string | null; // 隐藏宝箱图片
    hiddenTreasureChestId: number | null; // 隐藏宝箱ID
    closeRechargeModal: boolean; // 是否关闭充值模态框
}

interface AuthTokenData {
    token: string;
    refresh_token: string;
    salt: string;
}

/**
 * Pinia action 内部通过 this 调用其他 getter/action 时使用的上下文类型。
 * 显式声明可以避免 TypeScript 将 this 退化为仅包含 state 的对象。
 */
type UserActionContext = UnwrapRef<UserState> & {
    readonly IsLogin: boolean | string | undefined;
    saveToken(data: AuthTokenData): void;
    clearToken(): void;
    handleGetUserInfo(): Promise<unknown>;
    USER_BALANCE_RECEIVE(balance: number | string): void;
    USER_LOTTER_MONEY(balance: number | string): void;
    USER_AGENT_MONEY(agentMoney: number | string): void;
    USER_CAN_WITHDRAW_MONEY(canWithdrawMoney: number | string): void;
    setUnreadCount(data: number): void;
    setMessageDialog(data: MessageDialogType[]): void;
};

/**
 * 创建用户仓库的初始状态。
 */
function createUserState(): UserState {
    return {
        userInfo: HiCache.getCache(HiStance.USER_INFO) || null,
        salt: '',
        identificationTypeList: [],
        // token: HiCache.getCache(HiStance.TOKEN),
        token: HiCache.getCache(HiStance.TOKEN),
        refreshToken: HiCache.getCache(HiStance.REFRESH_TOKEN),
        loginConfig: {},
        rouletteList: {},
        homeCarouselList: [],
        openLoginDialogType: false,
        openSlider: false,
        openMessage: false,
        openForgotDialogType: false,
        openTurnTableDialogType: false,
        openLanguageType: false,
        rouletteFindTimeList: [],
        leaderboardList: [],
        dailyTasksList: [],
        sumPreferentialMoney: {},
        sliderActive: [],
        activityDetail: {},
        userBalance: '',
        agentMoney: '',
        getFastList: [],
        userImgList: [
            {
                id: 1,
                user_img: 'home/userImg1.png',
            },
            {
                id: 2,
                user_img: 'home/userImg2.png',
            },
            {
                id: 3,
                user_img: 'home/userImg3.png',
            },
            {
                id: 4,
                user_img: 'home/userImg4.png',
            },
            {
                id: 5,
                user_img: 'home/userImg5.png',
            },
            {
                id: 6,
                user_img: 'home/userImg6.png',
            },
            {
                id: 7,
                user_img: 'home/userImg7.png',
            },
            {
                id: 8,
                user_img: 'home/userImg8.png',
            },
            {
                id: 9,
                user_img: 'home/userImg9.png',
            },
            {
                id: 10,
                user_img: 'home/userImg10.png',
            },
            {
                id: 11,
                user_img: 'home/userImg11.png',
            },
            {
                id: 12,
                user_img: 'home/userImg12.png',
            },
            {
                id: 13,
                user_img: 'home/userImg13.png',
            },
            {
                id: 14,
                user_img: 'home/userImg14.png',
            },
            {
                id: 15,
                user_img: 'home/userImg15.png',
            },
            {
                id: 16,
                user_img: 'home/userImg16.png',
            },
            {
                id: 17,
                user_img: 'home/userImg17.png',
            },
            {
                id: 18,
                user_img: 'home/userImg18.png',
            },
            {
                id: 19,
                user_img: 'home/userImg19.png',
            },
            {
                id: 20,
                user_img: 'home/userImg20.png',
            },
            {
                id: 21,
                user_img: 'home/userImg21.png',
            },
            {
                id: 22,
                user_img: 'home/userImg22.png',
            },
            {
                id: 23,
                user_img: 'home/userImg23.png',
            },
            {
                id: 24,
                user_img: 'home/userImg24.png',
            },
            {
                id: 25,
                user_img: 'home/userImg25.png',
            },
            {
                id: 26,
                user_img: 'home/userImg26.png',
            },
            {
                id: 27,
                user_img: 'home/userImg27.png',
            },
            {
                id: 28,
                user_img: 'home/userImg28.png',
            },
            {
                id: 29,
                user_img: 'home/userImg29.png',
            },
            {
                id: 30,
                user_img: 'home/userImg30.png',
            },
            {
                id: 31,
                user_img: 'home/userImg31.png',
            },
            {
                id: 32,
                user_img: 'home/userImg32.png',
            },
            {
                id: 33,
                user_img: 'home/userImg33.png',
            },
            {
                id: 34,
                user_img: 'home/userImg34.png',
            },
            {
                id: 35,
                user_img: 'home/userImg35.png',
            },
            {
                id: 36,
                user_img: 'home/userImg36.png',
            },
            {
                id: 37,
                user_img: 'home/userImg37.png',
            },
            {
                id: 38,
                user_img: 'home/userImg38.png',
            },
            {
                id: 39,
                user_img: 'home/userImg39.png',
            },
            {
                id: 40,
                user_img: 'home/userImg40.png',
            },
            {
                id: 41,
                user_img: 'home/userImg41.png',
            },
            {
                id: 42,
                user_img: 'home/userImg42.png',
            },
            {
                id: 43,
                user_img: 'home/userImg43.png',
            },
            {
                id: 44,
                user_img: 'home/userImg44.png',
            },
            {
                id: 45,
                user_img: 'home/userImg45.png',
            },
            {
                id: 46,
                user_img: 'home/userImg46.png',
            },
            {
                id: 47,
                user_img: 'home/userImg47.png',
            },
            {
                id: 48,
                user_img: 'home/userImg48.png',
            },
            {
                id: 49,
                user_img: 'home/userImg49.png',
            },
            {
                id: 50,
                user_img: 'home/userImg50.png',
            },
        ],
        readNum: 0,
        userMainBanlance: '',
        turnTableId: 0,
        loginDialogType: 1,
        vipList: [],
        vipListInfo: '',
        homeActivity: [],
        homeNoticeList: [],
        openNoticeDialog: true,
        openActivityNotice: {},
        unreadCount: 0,
        messageDialog: [],
        isLoginSignIn: false,
        signInPreferentialActivityId: null,
        signInLevel: null,
        signInIcon: null,
        signInDynamicIcon: null,
        isOpenHiddenTreasureChest: false,
        hiddenTreasureChestDynamicIcon: null,
        hiddenTreasureChestIcon: null,
        hiddenTreasureChestId: null,
        closeRechargeModal: false,
    };
}

const userGetters = {
    GetUserInfo: (state: UserState) => state.userInfo,
    IdentificationTypeList: state => state.identificationTypeList,
    IsLogin: state => state.token && state.token?.length > 0,
    GetLoginConfig: state => state.loginConfig,
    GetRouletteList: state => state.rouletteList,
    getHomeCarouselList: (state: UserState) => state.homeCarouselList,
    OpenLoginDialogType: (state: UserState) => state.openLoginDialogType,
    OpenSlider: (state: UserState) => state.openSlider,
    OpenMessage: (state: UserState) => state.openMessage,
    OpenForgotDialogType: (state: UserState) => state.openForgotDialogType,
    RouletteFindTimeList: (state: UserState) => state.rouletteFindTimeList,
    LeaderboardList: (state: UserState) => state.leaderboardList,
    DailyTasksList: (state: UserState) => state.dailyTasksList,
    SumPreferentialMoney: (state: UserState) => state.sumPreferentialMoney,
    SliderActive: (state: UserState) => state.sliderActive,
    OpenTurnTableDialogType: (state: UserState) => state.openTurnTableDialogType,
    ActivityDetail: (state: UserState) => state.activityDetail,
    getUserBalance: (state: UserState) => state.userBalance,
    GetFastList: (state: UserState) => state.getFastList,
    OpenLanguageType: (state: UserState) => state.openLanguageType,
    GetUserImgList: (state: UserState) => state.userImgList,
    getSalt: (state: UserState) => state.salt,
    ReadNum: (state: UserState) => state.readNum,
    getUserMainBanlance: (state: UserState) => state.userMainBanlance,
    getTurnTableId: (state: UserState) => state.turnTableId,
    getLoginDialogType: (state: UserState) => state.loginDialogType,
    getVipList: (state: UserState) => state.vipList,
    getVipListInfo: (state: UserState) => state.vipListInfo,
    getHomeActivity: (state: UserState) => state.homeActivity,
    getHomeNoticeList: (state: UserState) => state.homeNoticeList,
    getOpenNoticeDialog: (state: UserState) => state.openNoticeDialog,
    getOpenActivityNotice: (state: UserState) => state.openActivityNotice,
    GetUnreadCount: state => state.unreadCount,
    GetMessageDialog: state => state.messageDialog,
    GetCloseRechargeModal: (state: UserState) => state.closeRechargeModal,
};

const userActions = {
    setCloseRechargeModal(data: boolean) {
        this.closeRechargeModal = data;
    },
    // 设置未读消息数量
    setUnreadCount(data: number) {
        this.unreadCount = data;
    },
    // 设置站内信弹窗数据
    setMessageDialog(data: MessageDialogType[]) {
        this.messageDialog = data;
    },
    // 打开登录弹窗
    changeOpenLoginDialogType(data: boolean, isRegister = 1) {
        this.openLoginDialogType = data;
        this.loginDialogType = isRegister;
    },
    // 切换登录注册弹窗
    changeLoginDialogType(data: number) {
        this.loginDialogType = data;
    },
    // 获取id
    changeTurnTableId(data: number | string) {
        this.turnTableId = data;
    },
    // 打开侧边菜单
    changeOpenSlider(data: boolean) {
        this.openSlider = data;
    },
    // 打开侧边消息列表
    changeOpenMessage(data: boolean) {
        this.openMessage = data;
    },
    // 点击多语言弹窗
    changeLanguagePopup(data: boolean) {
        this.openLanguageType = data;
    },
    // 打开忘记密码弹窗
    changeOpenForgotDialogType(data: boolean) {
        this.openForgotDialogType = data;
    },
    // 打开轮盘弹窗
    changeOpenTurnTableDialogType(data: boolean) {
        this.openTurnTableDialogType = data;
    },
    // 打开首页公告
    changeOpenNoticeDialog(data: boolean) {
        // notice_time
        this.openNoticeDialog = data;
    },
    // 打开活动公告
    changeOpenActivityNotice(data: OpenHomeActivityIdType) {
        // notice_time
        this.openActivityNotice = data;
        console.log('openActivityNotice---------------------------------------', data);
    },
    // 更新用户信息
    updateUserInfo(data: UserInfoType) {
        this.userInfo = data;
        localStorage.setItem('userInfo', JSON.stringify(data || ''));
    },
    // 保存token
    saveToken(data: AuthTokenData) {
        HiCache.setCache<string>(HiStance.TOKEN, data.token);
        HiCache.setCache<string>(HiStance.REFRESH_TOKEN, data.refresh_token);
        HiCache.setCache<string>(HiStance.SALT, data.salt);
        this.token = data.token;
        this.refreshToken = data.refresh_token;
        this.salt = data.salt;
    },
    // 保存token
    setToken(token: string) {
        if (token.includes('"')) {
            this.token = JSON.parse(token);
        } else {
            this.token = token;
        }
    },
    // 退出登录后清除token
    clearToken() {
        HiCache.deleteCache(HiStance.TOKEN);
        HiCache.deleteCache(HiStance.REFRESH_TOKEN);
        HiCache.deleteCache(HiStance.USER_INFO);
        this.token = undefined;
        this.refreshToken = undefined;
        this.userInfo = null;
        // 重置登录宝箱相关状态，避免未登录时仍然展示
        this.isLoginSignIn = false;
        this.signInPreferentialActivityId = null;
        this.signInLevel = null;
        this.isOpenHiddenTreasureChest = false;

        // 🎯 清除父页面的缓存备份
        try {
            window.parent.postMessage({
                type: 'CACHE_CLEAR'
            }, '*');
            console.log('✅ 已通知父页面清除缓存');
        } catch (error) {
            console.log('通知父页面清除缓存失败:', error);
        }
    },
    // 设置用户余额
    USER_BALANCE_RECEIVE(balance: any) {
        this.userBalance = balance;
        (this.userInfo as UserInfoType).money = balance;
    },
    // 设置彩票余额
    USER_LOTTER_MONEY(balance: any) {
        this.userMainBanlance = balance;
    },
    // 设置用户余额
    USER_AGENT_MONEY(agentMoney: any) {
        this.agentMoney = agentMoney;
    },
    // 设置用户余额
    USER_CAN_WITHDRAW_MONEY(canWithdrawMoney: any) {
        (this.userInfo as UserInfoType).canWithdrawMoney = canWithdrawMoney;
    },
    // 设置宝箱状态
    setPreferentialManualGiftCount() {
        (this.userInfo as UserInfoType).preferentialManualGiftCount = -1;
    },
    // 登录
    async handleLogin(this: UserActionContext, data: Parameters<typeof user.login>[0]) {
        try {
            const result = await user.login(data);
            if (result.code === 0) {
                this.saveToken(result.data);
                await this.handleGetUserInfo();
            }
            return result;
        } catch (e) {
            await Promise.reject(e);
        }
    },
    // Google快捷登录
    async handleGetGoogleLogin(data: unknown) {
        try {
            const { language } = useAppStore();
            const result = await user.getGoogleLogin({ data, language });
            return result;
        } catch (e) {
            await Promise.reject(e);
        }
    },
    // 快捷登录注册
    async handleGetFastRegister(data: Record<string, unknown>) {
        try {
            const { language } = useAppStore();
            const result = await user.getFastRegister({ data, language });
            return result;
        } catch (e) {
            await Promise.reject(e);
        }
    },
    // 获取用户信息
    async handleGetUserInfo(this: UserActionContext) {
        try {
            const result = await user.getUserInfo();
            if (result.code === 0) {
                this.userInfo = result.data;
                if (Array.isArray(this.userImgList) && this.userImgList?.length > 0) {
                    this.userInfo!.headPortrait = result.data?.headPortrait || this.userImgList[0]?.user_img;
                }
                // mock数据
                // this.userInfo!.preferentialManualGiftConfigSn = '1234567890';
                // this.userInfo!.preferentialManualGiftReceiveModel = 2;
                // this.userInfo!.preferentialManualGiftCount = 1;
                // this.userInfo!.preferentialManualGiftInfo = {
                //     maxMultipleMoney: 1000,
                //     btnName: '领取',
                //     isHot: true,
                //     multiEndImg: '/506/1784963811224.jpg',
                //     content: 'https://www.baidu.com',
                //     money: 100,
                //     startImg : "/506/1784964004240.jpg",
                //     openImg : "/506/1784964015425.jpg",
                //     endImg : "/506/1786012785139.png",
                //     title: 'Multiplier Rewards',
                //     description: 'You must deposit through this interface to receive Multiplier the bonus',
                //     ruleList: [
                //         {
                //             rechargeMoney: 100,
                //             minMultiple: 1,
                //             maxMultiple: 10,
                //             withdrawMultiple: 1,
                //         },
                //         {
                //             rechargeMoney: 200,
                //             minMultiple: 2,
                //             maxMultiple: 20,
                //             withdrawMultiple: 2,
                //         },
                //         {
                //             rechargeMoney: 300,
                //             minMultiple: 3,
                //             maxMultiple: 30,
                //             withdrawMultiple: 3,
                //         },
                //         {
                //             rechargeMoney: 400,
                //             minMultiple: 4,
                //             maxMultiple: 40,
                //             withdrawMultiple: 4,
                //         },
                //     ],
                // };
                const balanceOther = Number(parseFloat(result.data.money).toFixed(3).slice(0, -1));
                const agentBalance = Number(parseFloat(result.data.agentMoney).toFixed(3).slice(0, -1));
                this.USER_BALANCE_RECEIVE(balanceOther);
                this.USER_AGENT_MONEY(agentBalance);
                localStorage.setItem('userInfo', JSON.stringify(result.data || ''));

                // 保存登录宝箱相关数据
                this.isLoginSignIn = result.data?.isLoginSignIn || false;
                this.signInPreferentialActivityId = result.data?.signInPreferentialActivityId || null;
                this.signInLevel = result.data?.signInLevel || null;
                this.signInIcon = result.data?.signInIcon || null;
                this.signInDynamicIcon = result.data?.signInDynamicIcon || null;
                this.isOpenHiddenTreasureChest = result.data?.isOpenHiddenTreasureChest || false;
                this.hiddenTreasureChestDynamicIcon = result.data?.hiddenTreasureChestDynamicIcon || null;
                this.hiddenTreasureChestIcon = result.data?.hiddenTreasureChestIcon || null;
                this.hiddenTreasureChestId = result.data?.hiddenTreasureChestId || null;
                // HiCache.setCache<any>(HiStance.ALL_GAMES, result.da ta)
            }
            return result;
        } catch (e) {
            await Promise.reject(e);
        }
    },
    // 获取证件类型
    async handleGetIdentificationTypeEnums() {
        try {
            const result = await user.getIdentificationTypeEnums();
            if (result.code === 0) {
                this.identificationTypeList = result.data;
            }
            return result;
        } catch (e) {
            await Promise.reject(e);
        }
    },
    // 注册
    async handleRegister(this: UserActionContext, data: Parameters<typeof user.register>[0]) {
        try {
            const result = await user.register(data);
            if (result.code === 0) {
                this.saveToken(result.data);
                await this.handleGetUserInfo();
            }
            return result;
        } catch (e) {
            await Promise.reject(e);
        }
    },

    // 刷新token
    async getRefreshToken(this: UserActionContext, data: Parameters<typeof user.refreshToken>[0]) {
        try {
            const { getClientType } = usePwaClient();

            const result = await user.refreshToken({
                ...data,
                client: getClientType(),
            });
            if (result.code === 0) {
                this.saveToken(result.data);
            }
            return result;
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 用户退出登录
    async userLogout(this: UserActionContext) {
        try {
            const result = await user.logout();
            if (result.code === 0) {
                this.clearToken();
            }
            return result;
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取系统验证码
    async getCaptcha() {
        try {
            return await user.getCaptcha();
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 登录次数上限后是否显示系统验证码
    async handleGetIsShowCaptcha() {
        try {
            return await user.getIsShowCaptcha();
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取登录验证码配置
    async handleGetLoginConfig() {
        try {
            const res = await user.getLoginConfig();
            if (res && res?.code === 0) {
                this.loginConfig = res?.data;
            }
            return res;
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取轮盘奖品信息
    async handleGetUserRoulette() {
        try {
            const res = await user.getUserRoulette();
            // console.log(res, 'res--------');
            if (res && res?.code === 0) {
                this.rouletteList = res?.data;
            } else {
                this.rouletteList = {};
            }
            return res;
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取轮盘奖品信息
    async handleGetUserRouletteById(data: Parameters<typeof user.getUserRouletteById>[0]) {
        try {
            const res = await user.getUserRouletteById(data);
            // console.log(res, 'res--------');
            if (res && res?.code === 0) {
                this.rouletteList = res?.data;
            } else {
                this.rouletteList = {};
            }
            return res;
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取轮盘抽奖结果
    async handleGetRouletteStart(data: Parameters<typeof user.getRouletteStart>[0]) {
        try {
            return await user.getRouletteStart(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取轮盘抽奖列表
    async handleGetRouletteFindTime(data: Parameters<typeof user.getRouletteFindTime>[0]) {
        try {
            const res = await user.getRouletteFindTime(data);
            if (res && res?.code === 0) {
                this.rouletteFindTimeList = res?.data || [];
            }
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取轮盘抽奖列表
    async handleGetLeaderboardList(data: Parameters<typeof user.getLeaderboardList>[0]) {
        try {
            const res = await user.getLeaderboardList(data);
            if (res && res?.code === 0) {
                this.leaderboardList = res?.data || [];
            }
        } catch (error) {
            await Promise.reject(error);
        }
    },
    async getCarouselListTop() {
        const result = await user.getCarouselListTop();
        if (result.data) {
            this.homeCarouselList = result.data?.home;
            this.sliderActive = result.data?.homeLeft;
        }
    },
    // 站内消息数据
    async handleGetMyMessageNotice(params = { current: 1, size: 10 }) {
        try {
            return await user.getMyMessageNoticeList(params);
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 站内消息未读条数
    async handleGetUnReadCountMessage() {
        try {
            const res = await user.getUnReadCountMessage();
            if (res?.code === 0) {
                this.readNum = res?.data || 0;
            } else {
                this.readNum = 0;
            }
            return res;
        } catch (error) {
            await Promise.reject(error);
        }
    },
    /**
     * 已登录状态获取手机或邮箱验证码
     *     Register(1,"注册"),
     *     ForgetLoginPwd(2,"忘记登录密码"),
     *     ForgetWithdrawPwd(3,"忘记提现密码"),
     *     EditLoginPwd(4,"修改登录密码"),
     *     EditWithdrawPwd(5,"修改提现密码"),
     *     AddPhone(6,"绑定手机"),
     *     AddEmail(7,"绑定邮箱"),
     *     EditPhone(8,"更新手机"),
     *     EditEmail(9,"更换邮箱"),
     *     EditInfo(10,"完善信息");
     */
    async getSendCodeByUserId(data: Parameters<typeof user.getSendCodeByUserId>[0]) {
        try {
            return await user.getSendCodeByUserId(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },
    /**
     * 未登录状态发送手机或邮箱验证码
     *     Register(1,"注册"),
     *     ForgetLoginPwd(2,"忘记登录密码"),
     *     ForgetWithdrawPwd(3,"忘记提现密码"),
     *     EditLoginPwd(4,"修改登录密码"),
     *     EditWithdrawPwd(5,"修改提现密码"),
     *     AddPhone(6,"绑定手机"),
     *     AddEmail(7,"绑定邮箱"),
     *     EditPhone(8,"更新手机"),
     *     EditEmail(9,"更换邮箱"),
     *     EditInfo(10,"完善信息");
     */
    async getSendCode(data: Parameters<typeof user.getSendCode>[0]) {
        try {
            return await user.getSendCode(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },
    /**
     * 忘记密码
     */
    async handleGetForgetPassWord(data: Parameters<typeof user.getForgetPassWord>[0]) {
        try {
            return await user.getForgetPassWord(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },
    /**
     * 修改提现密码
     */
    async handleGetModifyPassword(data: Parameters<typeof user.modifyPassword>[0]) {
        try {
            return await user.modifyPassword(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },
    /**
     * 创建提现密码
     */
    async handleGetAddMoneyPassword(data: Parameters<typeof user.addMoneyPassword>[0]) {
        try {
            return await user.addMoneyPassword(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },

    /**
     * 每日任务列表
     */
    async handleGetFindTaskList() {
        try {
            const res = await user.getFindTaskList();
            if (res && res?.code === 0) {
                this.dailyTasksList = res?.data;
            }
            return res;
        } catch (error) {
            await Promise.reject(error);
        }
    },
    /**
     * 每日任务列表
     */
    async handleGetFindTaskApply(data: Parameters<typeof user.getFindTaskApply>[0]) {
        try {
            return await user.getFindTaskApply(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },
    async getUserVipList() {
        try {
            return await user.getUserVipList();
        } catch (error) {
            return error;
        }
    },
    //获取用户数量和返利金额
    async handleGetSumPreferentialMoney() {
        try {
            const res = await user.getSumPreferentialMoney();
            if (res && res?.code === 0) {
                this.sumPreferentialMoney = res?.data;
            }
            return res;
        } catch (error) {
            await Promise.reject(error);
        }
    },
    //活动详情页
    async handleGetContentAdsFindById(data: Parameters<typeof user.getContentAdsFindById>[0]) {
        try {
            const res = await user.getContentAdsFindById(data);
            if (res && res?.code === 0) {
                this.activityDetail = res?.data;
            }
            return res;
        } catch (error) {
            await Promise.reject(error);
        }
    },
    //消息已读
    async handleGetMyMessageRead(data: Parameters<typeof user.getMyMessageRead>[0]) {
        try {
            const res = await user.getMyMessageRead(data);
            if (res?.code === 0) {
                this.readNum = res?.data;
            }
            return res;
        } catch (error) {
            await Promise.reject(error);
        }
    },
    //获取用户余额
    async getRefreshBalance(this: UserActionContext) {
        try {
            const res = await user.getRefreshBalance();
            if (res && res?.code === 0 && Number(res?.data?.money) !== -1) {
                const balanceOther = Number(parseFloat(res.data.money).toFixed(3).slice(0, -1));
                const angentBalance = Number(parseFloat(res.data.agentMoney).toFixed(3).slice(0, -1));
                const walletMoney = Number(parseFloat(res.data.walletMoney).toFixed(3).slice(0, -1));
                const canWithdrawMoney = Number(parseFloat(res.data.canWithdrawMoney).toFixed(3).slice(0, -1));
                this.USER_BALANCE_RECEIVE(balanceOther);
                this.USER_AGENT_MONEY(angentBalance);
                this.USER_CAN_WITHDRAW_MONEY(canWithdrawMoney);
                // this.updateUserInfo({
                //     ...this.userInfo,
                //     money: this.userBalance,
                // } as UserInfoType)
                // this.handleGetUserInfo();
            }
            return res;
        } catch (error) {
            await Promise.reject(error);
        }
    },

    //获取平台余额
    async handleGetRecycleBalance(data: Parameters<typeof user.getRecycleBalance>[0]) {
        try {
            return await user.getRecycleBalance(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },

    async getNotUserInfo() {
        try {
            const result = await user.getNotUserInfo();
            if (result.code === 0) {
                this.userInfo = result.data;
                if (Array.isArray(this.userImgList) && this.userImgList?.length > 0) {
                    this.userInfo!.headPortrait = result.data?.headPortrait || this.userImgList[0]?.user_img;
                }
                localStorage.setItem('userInfo', JSON.stringify(result.data || ''));
            }
            return result;
        } catch (e) {
            await Promise.reject(e);
        }
    },
    // 获取用户余额
    getBalanceByUser(this: UserActionContext) {
        return new Promise((resolve, reject) => {
            const platform = {
                company: 0,
            };
            user.getBalanceByUser(platform)
                .then(res => {
                    if (res) {
                        if (res.code === 0 || res.code === 20 || res.code === 50) {
                            const balanceOther = Number(parseFloat(res.data).toFixed(3).slice(0, -1));
                            this.USER_BALANCE_RECEIVE(balanceOther);
                        }
                    }
                    resolve(res);
                })
                .catch(err => {
                    // console.log(err);
                    reject(err);
                });
        });
    },

    // 获取用户余额
    requestQueryCreditMoney(this: UserActionContext, company: number) {
        return new Promise((resolve, reject) => {
            const platform = {
                company,
            };
            user.getBalanceByUser(platform)
                .then(res => {
                    if (res && res.code === 0) {
                        const balanceOther = Number(parseFloat(res.data).toFixed(3).slice(0, -1));
                        this.USER_BALANCE_RECEIVE(balanceOther);
                        resolve(balanceOther);
                    }
                    resolve(0);
                })
                .catch(err => {
                    reject(err);
                });
        });
    },
    // 获取彩票余额
    async handleGetLotteryUserInfo(this: UserActionContext) {
        const res = await user.getLotteryUserInfo();
        if (res?.code === 0 && Number(res?.data?.officialLotteryMoney || 0) !== -1) {
            this.USER_LOTTER_MONEY(res?.data?.officialLotteryMoney || 0);
        }
    },
    // 获取用户余额
    async handleGetRefreshBalance(this: UserActionContext): Promise<unknown> {
        return new Promise((resolve, reject) => {
            user.getRefreshBalance()
                .then(res => {
                    if (res) {
                        if (res.code === 0 && res?.data?.money !== -1) {
                            const balanceOther = Number(parseFloat(res.data.money).toFixed(3).slice(0, -1));
                            const angentBalance = Number(parseFloat(res.data.agentMoney).toFixed(3).slice(0, -1));
                            this.USER_BALANCE_RECEIVE(balanceOther);
                            this.USER_AGENT_MONEY(angentBalance);
                        }
                    }
                    resolve(res);
                })
                .catch(err => {
                    // console.log(err);
                    reject(err);
                });
        });
    },

    //获取vip列表
    async getLevelActivityList() {
        try {
            const res = await user.getLevelActivityList();
            if (res?.code === 0 && res?.data?.result?.length > 0) {
                if (res.data?.result?.length > 0) {
                    this.vipList = res.data?.result?.map(element => {
                        let newItem = element?.item?.filter(item => item?.activityTypeName != 'washCode');
                        if ((newItem?.length || 0) > 3) {
                            newItem = newItem?.filter(item => newItem?.length > 3 && item?.activityType != 5);
                        }
                        element.item = newItem;
                        if (element?.item?.length > 0) {
                            let newValue = {};
                            for (const item of element?.item) {
                                newValue[item['activityTypeName']] = item?.content;
                            }
                            return {
                                ...element,
                                ...newValue,
                            };
                        } else {
                            return element;
                        }
                    });
                    console.log(this.vipList, 'this.vipList');
                } else {
                    this.vipList = [];
                }
            }

            this.vipListInfo = res?.data?.info || '';
        } catch (error) {
            await Promise.reject(error);
        }
    },

    //获取vip列表
    async signLevelActivity(data: Parameters<typeof user.signLevelActivity>[0]) {
        try {
            return await user.signLevelActivity(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },

    //我的vip奖金领取记录
    async getMoneyFlowList(data: Parameters<typeof user.getMoneyFlowList>[0]) {
        try {
            return await user.getMoneyFlowList(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },

    // 优惠活动弹窗请求
    async fetchHomeActivity() {
        try {
            let res: { code: number; data: HomeActivityType[] };
            if (this.token) {
                res = await user.getLoginHomeActivity();
            } else {
                res = await user.getHomeActivity();
            }
            return res;
        } catch (e) {
            return Promise.reject(e);
        }
    },
    setHomeActivityList(data: HomeActivityType[]) {
        this.homeActivity = data;
    },
    setHomeNoticeList(data: HomeNoticeListType[]) {
        this.homeNoticeList = data;
    },
    // 首页公告弹窗
    async fetchHomeDialogNoticeList() {
        try {
            return await user.homeDialogNoticeList();
        } catch (e) {
            return Promise.reject(e);
        }
    },

    // 获取站内信未读消息
    async getUnreadMessageCount(this: UserActionContext) {
        try {
            if (!this.IsLogin) {
                return;
            }
            const res = await user.countUnreadCount();
            if (res.code === 0) {
                this.setUnreadCount(res.data);
            }
            return res;
        } catch (e) {
            return Promise.reject(e);
        }
    },
    // 获取站内信弹窗数据
    async getMessageDialog(this: UserActionContext) {
        try {
            if (!this.IsLogin) {
                return;
            }
            const res = await user.getMessageDialog();
            if (res.code === 0) {
                this.setUnreadCount(res.data?.unreadCount || 0);
                this.setMessageDialog(res.data?.list || []);
            }
            return res;
        } catch (e) {
            return Promise.reject(e);
        }
    },
    // 用户访问网站
    async handleUserVisit(data: Parameters<typeof user.userVisit>[0]) {
        try {
            return await user.userVisit(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取利息宝信息
    async handleProfitInfo() {
        try {
            return await user.getProfitInfo();
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取利息宝记录
    async handleProfitRecordList(data: Parameters<typeof user.getProfitRecordList>[0]) {
        try {
            return await user.getProfitRecordList(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取利息宝规则
    async handleProfitRules() {
        try {
            return await user.getProfitRules();
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 利息宝转入
    async handleProfitMoveIn(data: Parameters<typeof user.getProfitMoveIn>[0]) {
        try {
            return await user.getProfitMoveIn(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 利息宝提现
    async handleProfitWithdraw(data: Parameters<typeof user.getProfitWithdraw>[0]) {
        try {
            return await user.getProfitWithdraw(data);
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 利息宝领取
    async handleProfitReceive() {
        try {
            return await user.getProfitReceive();
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 利息宝开关
    async handleProfitIsOpen() {
        try {
            return await user.getProfitIsOpen();
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取系统时间
    async handleGetSystemTime() {
        try {
            return await user.getSystemTime();
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取首页展示前十用户数据
    async handleGetFindWinAccountTop() {
        try {
            return await user.getFindWinAccountTop();
        } catch (error) {
            await Promise.reject(error);
        }
    },
    // 获取右侧广告数据
    async handleGetRightBannerData() {
        try {
            return await user.getRightBannerData();
        } catch (error) {
            await Promise.reject(error);
        }
    },
} satisfies Record<string, (...args: never[]) => unknown> & ThisType<UserActionContext>;

type UserGetters = typeof userGetters & _GettersTree<UserState>;

export const useUserStore = defineStore<'user', UserState, UserGetters, typeof userActions>('user', {
    state: createUserState,
    getters: userGetters as UserGetters,
    actions: userActions,
});
